import { css, cx } from "@emotion/css";
import imageExtensions from "image-extensions";
import isUrl from "is-url";
import type { LucideIcon } from "lucide-react";
import React, { PropsWithChildren } from "react";
import ReactDOM from "react-dom";

interface BaseProps {
  className?: string;
}

export interface IButtonProp {
  format: string;
  icon: LucideIcon;
}

type SlateButtonProps = PropsWithChildren<
  React.HTMLAttributes<HTMLSpanElement> & {
    active?: boolean;
    reversed?: boolean;
  }
>;

export const SlateButton = React.forwardRef<HTMLSpanElement, SlateButtonProps>(
  ({ className, active = false, reversed = false, ...props }, ref) => (
    <span
      {...props}
      ref={ref}
      className={cx(
        className,
        css`
          cursor: pointer;
          color: ${reversed
            ? active
              ? "white"
              : "#aaa"
            : active
              ? "black"
              : "#ccc"};
        `
      )}
    />
  )
);

export const EditorValue = React.forwardRef<
  HTMLDivElement,
  PropsWithChildren<
    {
      value: any;
    } & BaseProps
  >
>(({ className, value, ...props }, ref) => {
  const textLines = value.document.nodes
    .map((node: { text?: string }) => node.text)
    .toArray()
    .join("\n");
  return (
    <div
      ref={ref}
      {...props}
      className={cx(
        className,
        css`
          margin: 30px -20px 0;
        `
      )}
    >
      <div
        className={css`
          font-size: 14px;
          padding: 5px 20px;
          color: #404040;
          border-top: 2px solid #eeeeee;
          background: #f8f8f8;
        `}
      />
      <div
        className={css`
          color: #404040;
          font: 12px monospace;
          white-space: pre-wrap;
          padding: 10px 20px;
          div {
            margin: 0 0 0.5em;
          }
        `}
      >
        {textLines}
      </div>
    </div>
  );
});

export const Icon = React.forwardRef<
  HTMLSpanElement,
  PropsWithChildren<BaseProps>
>(({ className, ...props }, ref) => (
  <span
    {...props}
    ref={ref}
    className={cx(
      "material-icons",
      className,
      css`
        font-size: 18px;
        vertical-align: text-bottom;
      `
    )}
  />
));

export const Instruction = React.forwardRef<
  HTMLDivElement,
  PropsWithChildren<BaseProps>
>(({ className, ...props }, ref) => (
  <div
    {...props}
    ref={ref}
    className={cx(
      className,
      css`
        white-space: pre-wrap;
        margin: 0 -20px 10px;
        padding: 10px 20px;
        font-size: 14px;
        background: #f8f8e8;
      `
    )}
  />
));

export const Menu = React.forwardRef<
  HTMLDivElement,
  PropsWithChildren<BaseProps>
>(({ className, ...props }, ref) => (
  <div
    {...props}
    ref={ref}
    className={cx(
      className,
      css`
        & > * {
          display: inline-block;
        }
        & > * + * {
          margin-left: 15px;
        }
      `
    )}
  />
));

export const Portal = ({ children }) =>
  typeof document === "object"
    ? ReactDOM.createPortal(children, document.body)
    : null;

export const Toolbar = React.forwardRef<
  HTMLDivElement,
  PropsWithChildren<BaseProps>
>(({ className, ...props }, ref) => (
  <Menu {...props} ref={ref} className={`relative mb-2 pb-2 ${className}`} />
));

export const isImageUrl = (url) => {
  if (!url) return false;
  if (!isUrl(url)) return false;
  const ext = new URL(url).pathname.split(".").pop();
  return ext ? imageExtensions.includes(ext) : false;
};
